import { type ErrorComponentProps, useRouter } from '@tanstack/react-router';
import { ArrowLeft, RotateCcw, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/NewButton';

/**
 * Branded fallback the router renders whenever a route — or anything inside it —
 * throws during render. Page-level errors are caught by the leaf route's boundary,
 * which sits *below* the app shell, so this renders inside the nav/header and the
 * rest of the app keeps working. Root-level errors (e.g. the shell itself) fill
 * the screen. The raw error is only shown in development; users get a friendly
 * message while the failure is logged via the router's `defaultOnCatch`.
 */
export function RouteErrorFallback({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  return (
    <div className="flex h-full min-h-[400px] w-full flex-col items-center justify-center bg-white p-6">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
          <TriangleAlert className="h-6 w-6 text-red-500" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Something went wrong</h2>
        <p className="mt-2 text-sm text-gray-600">
          This part of the page hit an unexpected error. It has been logged and we're looking into
          it — you can try again or go back, and the rest of the app should keep working.
        </p>

        {import.meta.env.DEV && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-xs text-gray-500">
              Error details (development only)
            </summary>
            <pre className="mt-2 max-h-60 overflow-auto rounded-sm bg-gray-100 p-2 text-xs text-gray-700">
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ''}
            </pre>
          </details>
        )}

        <div className="mt-6 flex items-center justify-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            icon={ArrowLeft}
            text="Go back"
            onClick={() => router.history.back()}
          />
          <Button type="button" size="sm" icon={RotateCcw} text="Try again" onClick={reset} />
        </div>
      </div>
    </div>
  );
}
